'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: number;
  title: string;
  description: string;
  type: string;
  size: string;
  icon: string;
  restricted: boolean;
  fileUrl?: string;
}

interface InvestorResourcesProps {
  className?: string;
}

const InvestorResources = ({ className = '' }: InvestorResourcesProps) => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const resources: Resource[] = [
    { id: 1, title: 'Investment Methodology Overview', description: 'Comprehensive 40-page document detailing our four-pillar approach, sector selection criteria, and risk management framework.', type: 'PDF', size: '3.2 MB', icon: 'DocumentTextIcon', restricted: false, fileUrl: '/downloads/investment-methodology.pdf' },
    { id: 2, title: 'Financial Projections Model', description: 'Detailed 20-year financial model with sensitivity analysis, scenario planning, and returns projections across investment phases.', type: 'Excel', size: '8.5 MB', icon: 'TableCellsIcon', restricted: true, fileUrl: '/downloads/financial-projections-model.xlsx' },
    { id: 3, title: 'Risk Assessment Framework', description: 'Institutional-grade risk analysis covering political, currency, operational, and impact risks with mitigation strategies.', type: 'PDF', size: '2.8 MB', icon: 'ShieldCheckIcon', restricted: true, fileUrl: '/downloads/risk-assessment-framework.pdf' },
    { id: 4, title: 'Due Diligence Checklist', description: 'Comprehensive checklist covering financial, legal, operational, and impact dimensions for investment evaluation.', type: 'PDF', size: '1.5 MB', icon: 'ClipboardDocumentCheckIcon', restricted: false, fileUrl: '/downloads/due-diligence-checklist.pdf' },
    { id: 5, title: 'Impact Measurement Framework', description: 'Detailed methodology for tracking and reporting development outcomes, aligned with international impact investing standards.', type: 'PDF', size: '2.1 MB', icon: 'ChartBarIcon', restricted: true, fileUrl: '/downloads/impact-measurement-framework.pdf' },
    { id: 6, title: 'Quarterly Performance Report Q4 2025', description: 'Latest quarterly update on portfolio performance, impact metrics, and market developments across focus sectors.', type: 'PDF', size: '4.7 MB', icon: 'DocumentChartBarIcon', restricted: true, fileUrl: '/downloads/quarterly-performance-report-q4-2025.pdf' },
  ];

  const handleResourceAction = (resource: Resource) => {
    if (resource.restricted) {
      setSelectedResource(resource);
      setSubmitted(false);
      setReason('');
      setIsModalOpen(true);
      return;
    }
    if (resource.fileUrl) {
      const link = document.createElement('a');
      link.href = resource.fileUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !selectedResource || !reason) return;

    setLoading(true);
    try {
      const res = await fetch('/api/investor-access-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          reason,
          resourceId: selectedResource.id,
          resourceTitle: selectedResource.title,
        }),
      });

      if (!res.ok) throw new Error('Failed to send request');
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Error sending request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`py-4 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mb-6">
          <h2 className="font-headline text-3xl lg:text-5xl font-semibold text-foreground mb-4">
            <span className="text-accent">Investor</span> Resources & Documentation
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed">
            Access comprehensive documentation supporting due diligence, investment committee preparation, and ongoing portfolio monitoring.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-12">
          {resources.map((resource) => (
            <div key={resource.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-institutional transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="hidden sm:flex w-12 h-12 bg-primary/10 rounded-lg items-center justify-center">
                  <Icon name={resource.icon as any} size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-2">
                    <h3 className="font-body font-semibold">{resource.title}</h3>
                    {resource.restricted && <span className="px-2 py-1 bg-warning/10 text-warning text-xs rounded">Restricted</span>}
                  </div>
                  <p className="text-sm text-text-secondary mb-4">{resource.description}</p>
                  <div className="flex justify-between items-center">
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="DocumentIcon" size={14} />
                        {resource.type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="ArrowDownTrayIcon" size={14} />
                        {resource.size}
                      </span>
                    </div>
                    <button
                      onClick={() => handleResourceAction(resource)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${resource.restricted ? 'bg-secondary text-white hover:bg-secondary/90' : 'bg-primary text-white hover:bg-primary/90'}`}
                    >
                      {resource.restricted ? 'Request Access' : 'Download'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {isModalOpen && selectedResource && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-lg w-full sm:max-w-lg p-6 mx-4">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="LockClosedIcon" size={24} className="text-primary" />
                <h3 className="font-headline text-xl font-semibold">Restricted Resource Access</h3>
              </div>
              <p className="text-sm text-text-secondary mb-6">
                This document is available to qualified institutional investors. Provide your email and your reason for requesting access. Our investor relations team will respond within 24 hours.
              </p>

              {!submitted ? (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="investor@institution.com"
                    className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary"
                  />
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    placeholder="Reason for requesting access"
                    className="w-full px-4 py-3 border border-border rounded-md focus:ring-2 focus:ring-primary"
                    rows={3}
                  />
                  <div className="flex flex-col sm:flex-row gap-3 justify-end">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border rounded-md">
                      Cancel
                    </button>
                    <button type="submit" disabled={loading} className="px-4 py-2 bg-secondary text-white rounded-md">
                      {loading ? 'Submitting…' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-success text-sm">
                  Request submitted. Our team will contact you shortly.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default InvestorResources;
